import React from 'react';
import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Button,
    Box,
    Chip,
} from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';

interface PricingSectionProps {
    onGetStarted: (planName: string) => void;
}

interface Plan {
    name: string;
    tagline: string;
    features: string[];
    highlighted?: boolean;
}

// Pricing is not finalized (no billing model has been decided for this product), so plans
// carry no price figures and no hard limits the product does not enforce today (there is no
// free-tier cap or plan-gating mechanism built yet). Each plan describes what it is intended
// to include, not an enforced entitlement.
const PRICING_NOTE = 'Pricing in preparation — talk to us';
const CTA = 'Talk to Us';

export const PRICING_PLANS: Plan[] = [
    {
        name: 'Free',
        tagline: 'See the benefit before you commit.',
        features: [
            'Sales invoices, quotations & receipts',
            'Item and stock records',
            'Customer records',
            'Owner dashboard',
        ],
    },
    {
        name: 'Starter',
        tagline: 'A fuller back office as you grow.',
        features: [
            'Everything in Free',
            'Stocktakes',
            'Stock transfers between stores',
            'Dashboards & reports',
        ],
    },
    {
        name: 'Growth',
        tagline: 'The full back office, store by store.',
        features: [
            'Everything in Starter',
            'Procurement — suppliers, purchase orders, goods receipts',
            'Supplier bills & payments',
            'Accounting & general ledger',
            'Bank',
        ],
        highlighted: true,
    },
    {
        name: 'Enterprise',
        tagline: 'For retail chains running many stores.',
        features: [
            'Everything in Growth',
            'Data import & on-site onboarding',
            'Priority support',
        ],
    },
];

const PricingSection: React.FC<PricingSectionProps> = ({ onGetStarted }) => (
    <section className="landing-pricing" id="pricing">
        <Container maxWidth="lg">
            <Box textAlign="center" mb={6}>
                <h2 className="landing-section-title">Plans for Every Store</h2>
                <Typography variant="body1" color="text.secondary" maxWidth={600} mx="auto">
                    We're finalizing pricing for retail. Talk to us and we'll help you find
                    the plan that fits your business.
                </Typography>
            </Box>
            <Grid container spacing={3} alignItems="stretch">
                {PRICING_PLANS.map((plan) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={plan.name}>
                        <Card
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                position: 'relative',
                                border: plan.highlighted ? '2px solid #007bff' : '1px solid #e0e0e0',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                    transform: 'translateY(-4px)',
                                    boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                },
                            }}
                            elevation={plan.highlighted ? 3 : 1}
                        >
                            {plan.highlighted && (
                                <Chip
                                    label="Best value"
                                    color="primary"
                                    size="small"
                                    sx={{
                                        position: 'absolute',
                                        top: 12,
                                        right: 12,
                                        fontWeight: 600,
                                    }}
                                />
                            )}
                            <CardContent
                                sx={{ p: 3, display: 'flex', flexDirection: 'column', flexGrow: 1 }}
                            >
                                <Typography variant="h6" fontWeight={600}>
                                    {plan.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" my={1}>
                                    {PRICING_NOTE}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={2}>
                                    {plan.tagline}
                                </Typography>
                                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, flexGrow: 1 }}>
                                    {plan.features.map((feature) => (
                                        <Box
                                            component="li"
                                            key={feature}
                                            sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                                        >
                                            <CheckCircleOutline sx={{ fontSize: 18, color: '#28a745' }} />
                                            <Typography variant="body2">{feature}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                                <Button
                                    variant={plan.highlighted ? 'contained' : 'outlined'}
                                    fullWidth
                                    onClick={() => onGetStarted(plan.name)}
                                    sx={{ mt: 2, textTransform: 'none', fontWeight: 600 }}
                                >
                                    {CTA}
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
        </Container>
    </section>
);

export default PricingSection;
